import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INSPECTION_FIRMS_REQUEST } from "./types";

interface InspectionFirmsState {
  loading: boolean;
  allInspectionFirms: any;
  inspectionFirmDetails: any | null;
  inspectionFirmAllUsers: any | null;
  taxDetails: any | null;
  taxClassificationTypes: any[];
  insurances: any[];
  services: any | null;
  inspectionServices: any[];
  coverageAreas: any[];
  users: any | null;
  inspectionFirmDocuments: any | null;
  error: string | null;
  ownershipTypes: any[];
  membershipTypes: any[];
  insuranceTypes: any[];
  certificationTypes: any[];
  certificationMethods: any[];
  currentInspectionFirmDetails: any | null;
  currentTaxDetails: any | null;
  currentInsurances: any[];
  currentServices: any | null;
  currentCoverageAreas: any[];
}

const initialState: InspectionFirmsState = {
  loading: false,
  allInspectionFirms: null,
  inspectionFirmDetails: null,
  inspectionFirmAllUsers: null,
  taxDetails: null,
  taxClassificationTypes: [],
  insurances: [],
  services: null,
  inspectionServices: [],
  coverageAreas: [],
  users: null,
  inspectionFirmDocuments: null,
  error: null,
  ownershipTypes: [],
  membershipTypes: [],
  insuranceTypes: [],
  certificationTypes: [],
  certificationMethods: [],
  currentInspectionFirmDetails: null,
  currentTaxDetails: null,
  currentInsurances: [],
  currentServices: null,
  currentCoverageAreas: [],
};

const inspectionFirmsSlice = createSlice({
  name: INSPECTION_FIRMS_REQUEST,
  initialState,
  reducers: {
    getAllInspectionFirmsRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    getAllInspectionFirmsSuccess: (state, action) => {
      state.loading = false;
      state.allInspectionFirms = action.payload;
      state.error = null;
    },
    getAllInspectionFirmsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getInspectionFirmDetailsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInspectionFirmDetailsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.inspectionFirmDetails = action.payload.response;
    },
    getInspectionFirmDetailsFailure(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOwnershipTypesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getOwnershipTypesSuccess: (state, action) => {
      state.loading = false;
      state.ownershipTypes = action.payload;
    },
    getOwnershipTypesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getMembershipTypesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getMembershipTypesSuccess: (state, action) => {
      state.loading = false;
      state.membershipTypes = action.payload;
    },
    getMembershipTypesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getInspectionFirmAllUsersRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInspectionFirmAllUsersSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.inspectionFirmAllUsers = action.payload.response;
    },
    getInspectionFirmAllUsersFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getTaxDetailsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getTaxDetailsSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.taxDetails = action.payload.response;
    },
    getTaxDetailsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getTaxClassificationsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getTaxClassificationsSuccess(state, action) {
      state.loading = false;
      state.taxClassificationTypes = action.payload;
    },
    getTaxClassificationsFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getInsuranceTypesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getInsuranceTypesSuccess: (state, action) => {
      state.loading = false;
      state.insuranceTypes = action.payload;
    },
    getInsuranceTypesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getInsurancesRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInsurancesSuccess(state, action) {
      state.loading = false;
      state.insurances = action.payload;
    },
    getInsurancesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getServicesRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getServicesSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.services = action.payload;
    },
    getServicesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getInspectionServicesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getInspectionServicesSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.inspectionServices = action.payload;
    },
    getInspectionServicesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getCertificationTypesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCertificationTypesSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.certificationTypes = action.payload;
    },
    getCertificationTypesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getCoverageAreasRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getCoverageAreasSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.coverageAreas = action.payload;
    },
    getCoverageAreasFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getInspectionFirmUsersRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInspectionFirmUsersSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.users = action.payload.response;
    },
    getInspectionFirmUsersFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getInspectionFirmDocumentsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInspectionFirmDocumentsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.inspectionFirmDocuments = action.payload.response;
    },
    getInspectionFirmDocumentsFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getCertificationMethodsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCertificationMethodsSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.certificationMethods = action.payload;
    },
    getCertificationMethodsFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getCurrentInspectionFirmDetailsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCurrentInspectionFirmDetailsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.currentInspectionFirmDetails = action.payload.response;
    },
    getCurrentInspectionFirmDetailsFailure(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getCurrentTaxDetailsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCurrentTaxDetailsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.currentTaxDetails = action.payload.response;
    },
    getCurrentTaxDetailsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getCurrentInsurancesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCurrentInsurancesSuccess(state, action) {
      state.loading = false;
      state.currentInsurances = action.payload;
    },
    getCurrentInsurancesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getCurrentServicesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCurrentServicesSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.currentServices = action.payload;
    },
    getCurrentServicesFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    getCurrentCoverageAreasRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getCurrentCoverageAreasSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.currentCoverageAreas = action.payload;
    },
    getCurrentCoverageAreasFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  getAllInspectionFirmsRequest,
  getAllInspectionFirmsSuccess,
  getAllInspectionFirmsFailure,
  getInspectionFirmDetailsRequest,
  getInspectionFirmDetailsSuccess,
  getInspectionFirmDetailsFailure,
  getOwnershipTypesRequest,
  getOwnershipTypesSuccess,
  getOwnershipTypesFailure,
  getMembershipTypesRequest,
  getMembershipTypesSuccess,
  getMembershipTypesFailure,
  getInspectionFirmAllUsersRequest,
  getInspectionFirmAllUsersSuccess,
  getInspectionFirmAllUsersFailure,
  getTaxDetailsRequest,
  getTaxDetailsSuccess,
  getTaxDetailsFailure,
  getTaxClassificationsRequest,
  getTaxClassificationsSuccess,
  getTaxClassificationsFailure,
  getInsuranceTypesRequest,
  getInsuranceTypesSuccess,
  getInsuranceTypesFailure,
  getInsurancesRequest,
  getInsurancesSuccess,
  getInsurancesFailure,
  getServicesRequest,
  getServicesSuccess,
  getServicesFailure,
  getInspectionServicesRequest,
  getInspectionServicesSuccess,
  getInspectionServicesFailure,
  getCertificationTypesRequest,
  getCertificationTypesSuccess,
  getCertificationTypesFailure,
  getCoverageAreasRequest,
  getCoverageAreasSuccess,
  getCoverageAreasFailure,
  getInspectionFirmUsersRequest,
  getInspectionFirmUsersSuccess,
  getInspectionFirmUsersFailure,
  getInspectionFirmDocumentsRequest,
  getInspectionFirmDocumentsSuccess,
  getInspectionFirmDocumentsFailure,
  getCertificationMethodsRequest,
  getCertificationMethodsSuccess,
  getCertificationMethodsFailure,
  getCurrentInspectionFirmDetailsRequest,
  getCurrentInspectionFirmDetailsSuccess,
  getCurrentInspectionFirmDetailsFailure,
  getCurrentTaxDetailsRequest,
  getCurrentTaxDetailsSuccess,
  getCurrentTaxDetailsFailure,
  getCurrentInsurancesRequest,
  getCurrentInsurancesSuccess,
  getCurrentInsurancesFailure,
  getCurrentServicesRequest,
  getCurrentServicesSuccess,
  getCurrentServicesFailure,
  getCurrentCoverageAreasRequest,
  getCurrentCoverageAreasSuccess,
  getCurrentCoverageAreasFailure,
} = inspectionFirmsSlice.actions;

export default inspectionFirmsSlice.reducer;
